import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format, addDays, parseISO } from 'date-fns';
import { Calendar as CalendarIcon, Clock, ChevronLeft, ChevronRight, CreditCard } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, isAuthenticated } = useAuth();
  
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    const preselectedService = searchParams.get('service');
    if (preselectedService && services.length > 0) {
      const service = services.find(s => s.slug === preselectedService);
      if (service) {
        setSelectedService(service);
        setStep(2);
      }
    }
  }, [searchParams, services]);

  useEffect(() => {
    if (selectedDate && selectedService) {
      fetchAvailableSlots();
    }
  }, [selectedDate, selectedService]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      toast.error('Failed to load services');
    }
  };

  const fetchAvailableSlots = async () => {
    setSlotsLoading(true);
    try {
      const response = await axios.get(`${API}/appointments/available-slots`, {
        params: {
          date: format(selectedDate, 'yyyy-MM-dd'),
          service_id: selectedService.id
        }
      });
      setAvailableSlots(response.data.slots);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load available slots');
      setAvailableSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleBooking = async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/book' } } });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/appointments`,
        {
          service_id: selectedService.id,
          date: format(selectedDate, 'yyyy-MM-dd'),
          time: selectedSlot.start,
          notes: notes || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const appointment = response.data;

      if (selectedService.price > 0) {
        // Create checkout session
        const checkoutResponse = await axios.post(
          `${API}/payments/create-checkout`,
          {
            appointment_id: appointment.id,
            origin_url: window.location.origin
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        window.location.href = checkoutResponse.data.url;
      } else {
        toast.success('Consultation booked successfully!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book appointment');
    } finally {
      setLoading(false);
    }
  };

  const minDate = addDays(new Date(), 1);

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="booking-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        {/* Progress Steps */}
        <div className="mb-12">
          <div className="flex items-center justify-center space-x-4">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                    step >= s
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                  }`}
                  data-testid={`step-indicator-${s}`}
                >
                  {s}
                </div>
                {s < 3 && (
                  <div className={`w-20 h-1 ${step > s ? 'bg-primary' : 'bg-muted'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="flex justify-center mt-4 text-sm text-muted-foreground">
            <span className={step === 1 ? 'text-primary font-medium' : ''}>Service</span>
            <span className="mx-8"></span>
            <span className={step === 2 ? 'text-primary font-medium' : ''}>Date & Time</span>
            <span className="mx-8"></span>
            <span className={step === 3 ? 'text-primary font-medium' : ''}>Confirm</span>
          </div>
        </div>

        {/* Step 1: Select Service */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <h1 className="font-heading text-4xl font-light mb-2">Choose Your Service</h1>
              <p className="text-muted-foreground">Select the type of therapy that suits your needs</p>
            </div>

            <div className="grid gap-4">
              {services.map((service) => (
                <div
                  key={service.id}
                  onClick={() => setSelectedService(service)}
                  className={`p-6 rounded-2xl border-2 cursor-pointer transition-all duration-300 ${
                    selectedService?.id === service.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid={`service-option-${service.slug}`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-heading text-xl mb-1">{service.name}</h3>
                      <p className="text-sm text-muted-foreground mb-2">{service.description}</p>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {service.duration_minutes} min
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-xl font-medium text-primary">
                        {service.price > 0 ? `£${service.price}` : 'Free'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end pt-6">
              <Button
                onClick={() => setStep(2)}
                disabled={!selectedService}
                className="rounded-full px-8"
                data-testid="step1-next-btn"
              >
                Continue
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 2: Select Date & Time */}
        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <h1 className="font-heading text-4xl font-light mb-2">Select Date & Time</h1>
              <p className="text-muted-foreground">Choose a convenient time for your {selectedService?.name}</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Calendar */}
              <div className="bg-card rounded-2xl p-6 shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)]">
                <h3 className="font-heading text-lg mb-4">Select Date</h3>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => {
                    setSelectedDate(date);
                    setSelectedSlot(null);
                  }}
                  disabled={(date) => date < minDate}
                  className="rounded-xl"
                  data-testid="booking-calendar"
                />
                <p className="text-xs text-muted-foreground mt-4">
                  Bookings must be made at least 24 hours in advance
                </p>
              </div>

              {/* Time Slots */}
              <div className="bg-card rounded-2xl p-6 shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)]">
                <h3 className="font-heading text-lg mb-4">Available Times</h3>
                {!selectedDate ? (
                  <p className="text-muted-foreground text-center py-8">
                    Please select a date first
                  </p>
                ) : slotsLoading ? (
                  <p className="text-muted-foreground text-center py-8">Loading slots...</p>
                ) : availableSlots.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No available slots for this date
                  </p>
                ) : (
                  <div className="grid grid-cols-3 gap-2 max-h-80 overflow-y-auto">
                    {availableSlots.map((slot) => (
                      <button
                        key={slot.start}
                        onClick={() => slot.available && setSelectedSlot(slot)}
                        disabled={!slot.available}
                        className={`py-2 px-3 rounded-xl text-sm font-medium transition-all ${
                          selectedSlot?.start === slot.start
                            ? 'bg-primary text-primary-foreground'
                            : slot.available
                            ? 'bg-secondary hover:bg-primary/10'
                            : 'bg-muted text-muted-foreground/50 cursor-not-allowed'
                        }`}
                        data-testid={`time-slot-${slot.start.replace(':', '')}`}
                      >
                        {slot.start}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-between pt-6">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="rounded-full px-8"
                data-testid="step2-back-btn"
              >
                <ChevronLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <Button
                onClick={() => setStep(3)}
                disabled={!selectedSlot}
                className="rounded-full px-8"
                data-testid="step2-next-btn"
              >
                Continue
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 3: Confirm Booking */}
        {step === 3 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <h1 className="font-heading text-4xl font-light mb-2">Confirm Your Booking</h1>
              <p className="text-muted-foreground">Review your appointment details</p>
            </div>

            <div className="bg-card rounded-2xl p-8 shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)]">
              <h3 className="font-heading text-2xl mb-6">Booking Summary</h3>
              
              <div className="space-y-4">
                <div className="flex justify-between py-3 border-b border-border">
                  <span className="text-muted-foreground">Service</span>
                  <span className="font-medium">{selectedService?.name}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-border">
                  <span className="text-muted-foreground">Date</span>
                  <span className="font-medium">
                    {selectedDate && format(selectedDate, 'EEEE, d MMMM yyyy')}
                  </span>
                </div>
                <div className="flex justify-between py-3 border-b border-border">
                  <span className="text-muted-foreground">Time</span>
                  <span className="font-medium">
                    {selectedSlot?.start} - {selectedSlot?.end}
                  </span>
                </div>
                <div className="flex justify-between py-3 border-b border-border">
                  <span className="text-muted-foreground">Duration</span>
                  <span className="font-medium">{selectedService?.duration_minutes} minutes</span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-muted-foreground">Total</span>
                  <span className="text-2xl font-medium text-primary">
                    {selectedService?.price > 0 ? `£${selectedService.price}` : 'Free'}
                  </span>
                </div>
              </div>

              <div className="mt-6">
                <Label htmlFor="notes">Additional Notes (optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Any information you'd like us to know before your session..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="mt-2 rounded-xl"
                  rows={3}
                  data-testid="booking-notes-input"
                />
              </div>

              {selectedService?.price > 0 && (
                <div className="mt-6 p-4 bg-secondary/50 rounded-xl">
                  <div className="flex items-center gap-2 text-sm">
                    <CreditCard className="h-4 w-4" />
                    <span>Secure payment via Stripe</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    You'll be redirected to complete payment securely
                  </p>
                </div>
              )}

              <p className="text-xs text-muted-foreground mt-6">
                By booking, you agree to our 24-hour cancellation policy. Appointments cancelled 
                with less than 24 hours notice may be subject to a cancellation fee.
              </p>
            </div>

            <div className="flex justify-between pt-6">
              <Button
                variant="outline"
                onClick={() => setStep(2)}
                className="rounded-full px-8"
                data-testid="step3-back-btn"
              >
                <ChevronLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <Button
                onClick={handleBooking}
                disabled={loading}
                className="rounded-full px-8"
                data-testid="confirm-booking-btn"
              >
                {loading ? 'Processing...' : selectedService?.price > 0 ? 'Proceed to Payment' : 'Confirm Booking'}
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default BookingPage;
